import './applianceImage.scss';

import React, { useCallback, useMemo, useState } from 'react';
import {
    faChevronLeft,
    faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Icon } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import areaPi2Icon from '../../images/icons/Icon-Superficie.svg';
import areaPi3Icon from '../../images/icons/icon-Size-pi3.svg';
import btuIcon from '../../images/icons/icon-BTU.svg';
import particleIcon from '../../images/icons/Icon-Particules.svg';
import sizeIcon from '../../images/icons/Icon-Size.svg';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';

type ApplianceImageProps = {
    appliance: any;
};

export default function ApplianceImage(props: ApplianceImageProps) {
    const { appliance } = props;
    const [images] = useState(appliance.images);
    const { currentLocale } = usePageLocalization();
    const arrowLeft =
        'M25.08,26.943,17,18.841l8.084-8.1L22.591,8.25,12,18.841,22.591,29.432Z';
    const arrowRight =
        'M12.885,27.318l8.084-8.1-8.084-8.1,2.489-2.489L25.965,19.216,15.374,29.807Z';
    const currentDevice = useMobileDetect();
    const [isImageSpecsOpen, setImageSpecsOpen] = useState(
        !currentDevice.isMobile()
    );
    const [currentImgIndex, setCurrentImgIndex] = useState<number>(0);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const maxBtu = appliance.attributes?.PWMaSoi?.values[0][currentLocale];
    const heatingArea = appliance.attributes?.Surfi?.values[0][currentLocale];
    const chamberVolume =
        appliance.attributes?.VolComi?.values[0][currentLocale];
    const emissionsAverage =
        appliance.attributes?.Emiss?.values[0][currentLocale];
    const chimneyDiameter =
        appliance.attributes?.LongBuci?.values[0][currentLocale];

    const currentImg = useMemo(
        () =>
            appliance?.fields?.images?.[currentImgIndex]?.childImageSharp
                ?.gatsbyImageData,
        [currentImgIndex]
    );

    const GetCurrentImg = useCallback(() => {
        if (typeof currentImg === 'string') {
            return (
                <>
                    <img src={currentImg} alt='' />
                    <Icon
                        d='M44.9,14.5c-0.4-2.2-2.3-3.8-4.5-4.3C37.1,9.5,31,9,24.4,9c-6.6,0-12.8,0.5-16.1,1.2c-2.2,0.5-4.1,2-4.5,4.3C3.4,17,3,20.5,3,25s0.4,8,0.9,10.5c0.4,2.2,2.3,3.8,4.5,4.3c3.5,0.7,9.5,1.2,16.1,1.2s12.6-0.5,16.1-1.2c2.2-0.5,4.1-2,4.5-4.3c0.4-2.5,0.9-6.1,1-10.5C45.9,20.5,45.4,17,44.9,14.5z M19,32V18l12.2,7L19,32z'
                        size='100'
                        fill='darkgrey'
                        viewbox='0 0 50 50'
                    />
                </>
            );
        } else {
            return <GatsbyImage image={currentImg} alt='' />;
        }
    }, [currentImg, images]);

    const getNextImageIndex = (index: number): number => {
        const start = 0;
        const end = appliance?.fields?.images?.length - 1;

        if (index > end) {
            return start;
        } else if (index < start) {
            return end;
        } else {
            return index;
        }
    };

    function swipeEvent() {
        let newIndex = currentImgIndex;

        if (touchStart - touchEnd > 75) {
            newIndex += 1;
        } else if (touchStart - touchEnd < -75) {
            newIndex -= 1;
        }

        setCurrentImgIndex(getNextImageIndex(newIndex));
    }

    const ImageAndSliders = useCallback(() => {
        return (
            <>
                {GetCurrentImg()}
                <div
                    className='sliders'
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                >
                    <FontAwesomeIcon
                        icon={faChevronLeft}
                        color={`#fd4239`}
                        className='arrow'
                        onClick={() => {
                            if (!currentDevice.isMobile()) {
                                setCurrentImgIndex((i) =>
                                    getNextImageIndex(i - 1)
                                );
                            }
                        }}
                        onTouchStart={() => {
                            if (currentDevice.isMobile()) {
                                setCurrentImgIndex((i) =>
                                    getNextImageIndex(i - 1)
                                );
                            }
                        }}
                    />
                    <div className='dots'>
                        {images?.map((img: any, index: number) => {
                            return (
                                <input
                                    type='radio'
                                    value={index.toString()}
                                    key={`appliance-img-${index}`}
                                    checked={currentImgIndex === index}
                                    onChange={() => {
                                        if (!currentDevice.isMobile()) {
                                            setCurrentImgIndex(index);
                                        }
                                    }}
                                    onTouchStart={() => {
                                        if (currentDevice.isMobile()) {
                                            setCurrentImgIndex(index);
                                        }
                                    }}
                                />
                            );
                        })}
                    </div>
                    <FontAwesomeIcon
                        icon={faChevronRight}
                        color={`#fd4239`}
                        className='arrow'
                        onClick={() => {
                            if (!currentDevice.isMobile()) {
                                setCurrentImgIndex((i) =>
                                    getNextImageIndex(i + 1)
                                );
                            }
                        }}
                        onTouchStart={() => {
                            if (currentDevice.isMobile()) {
                                setCurrentImgIndex((i) =>
                                    getNextImageIndex(i + 1)
                                );
                            }
                        }}
                    />
                </div>{' '}
            </>
        );
    }, [GetCurrentImg, appliance?.fields?.images, images]);

    const ApplianceImageSpecs = () => {
        return (
            <div
                className={
                    isImageSpecsOpen
                        ? 'appliance-image-specs open'
                        : 'appliance-image-specs'
                }
                onClick={(e) => {
                    e.stopPropagation();

                    if (!currentDevice.isMobile()) {
                        setImageSpecsOpen(!isImageSpecsOpen);
                    }
                }}
                onTouchStart={(e) => {
                    e.stopPropagation();

                    if (currentDevice.isMobile()) {
                        setImageSpecsOpen(!isImageSpecsOpen);
                    }
                }}
                onTouchMove={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
            >
                <Icon
                    d={isImageSpecsOpen ? arrowRight : arrowLeft}
                    size='12'
                    viewbox='0 0 13.08 21.182'
                    transform='translate(-12 -8.25)'
                    color='white'
                />
                <div className='appliance-infos'>
                    {maxBtu && (
                        <div>
                            <div className='infos'>
                                <img src={btuIcon} alt='' />
                                <span>{maxBtu}</span>
                            </div>
                            <div className='background' />
                        </div>
                    )}

                    {heatingArea && (
                        <div>
                            <div className='infos'>
                                <img src={areaPi2Icon} alt='' />
                                <span>{heatingArea}</span>
                            </div>
                            <div className='background' />
                        </div>
                    )}

                    {chamberVolume && (
                        <div>
                            <div className='infos'>
                                <img src={areaPi3Icon} alt='' />
                                <span>{chamberVolume}</span>
                            </div>
                            <div className='background' />
                        </div>
                    )}

                    {emissionsAverage && (
                        <div>
                            <div className='infos'>
                                <img src={particleIcon} alt='' />
                                <span>{emissionsAverage}</span>
                            </div>
                            <div className='background' />
                        </div>
                    )}

                    {chimneyDiameter && (
                        <div>
                            <div className='infos'>
                                <img src={sizeIcon} alt='' />
                                <span>{chimneyDiameter}</span>
                            </div>
                            <div className='background' />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className='appliance-image'>
            <div
                className='image'
                onTouchStart={(event) =>
                    setTouchStart(event.targetTouches[0].clientX)
                }
                onTouchMove={(event) =>
                    setTouchEnd(event.targetTouches[0].clientX)
                }
                onTouchEnd={() => {
                    swipeEvent();
                }}
            >
                <ImageAndSliders />

                <ApplianceImageSpecs />
            </div>
        </div>
    );
}
